/* eslint-disable no-nested-ternary */
/* eslint-disable no-use-before-define */
import { zoom, zoomIdentity, zoomTransform } from 'd3-zoom';
import { types as cursorTypes } from '../enums/cursor';
import { types as eventTypes } from '../enums/event';

/**
 * Create the zoom behavior
 * @param {ModchartsInteractive} chart - Modcharts chart instance
 * @returns {object} - D3 zoom behavior
 */
const createZoom = chart => {
	// The minimum number of steps (between adjacent data points) to show when zoomed in
	const minSteps = 5;

	return zoom()
		.on('start', event => {
			// Only set the scale extent once. The extent gets reset in the chart.days setter.
			if (chart.state?.xAxisPresentScale && chart.zoom.scaleExtent()[1] === Infinity) {
				// Get present domain bounds for comparison
				const [start, stop] = chart.state.xAxisPresentScale.domain();
				const domainLength = stop - start;

				// Calculate index step size between data points in initial time frame
				const firstDatumDateIndex = chart.closestExchangeIndex(chart.dataPrimary[0].date);
				const secondDatumDateIndex = chart.closestExchangeIndex(chart.dataPrimary[1].date);
				const indexStep = secondDatumDateIndex - firstDatumDateIndex;

				// The zoom in limit is the inverse of the ratio of the length of the minSteps to the total length (shown simplified)
				const zoomInBoundary = domainLength / (indexStep * minSteps);

				// Set the scale extent
				chart.zoom.scaleExtent([0, zoomInBoundary]);

				// NOTE: We don't need a zoom out boundary because that's handled by the left/right boundaries
			}
			onZoomStart(chart, event);
		})
		.on('zoom', event => {
			onZoom(chart, event);
		})
		.on('end', event => {
			onZoomEnd(chart, event);
		})
		.wheelDelta(event => {
			return wheelDelta(chart, event);
		});
};

/**
 * Calculates the resulting zoom transform given two different scales
 * @param {object} fromScale - The D3 scale with the domain that you're changing from
 * @param {object} toScale - The D3 scale with the domain that the transform will change to
 * @param {object} currentTransform - The current D3 zoom transform. Used to retrieve the current translation values. Defaults to zoomIdentity.
 * @param {string} scaleAxis - The axis associated with the scales: 'x' or 'y'. Defaults to 'x'.
 * @returns {object} D3 zoom transform that represents the modification required to get from the fromScale domain to the toScale domain
 */
export const getTransformFromScales = (fromScale, toScale, currentTransform = zoomIdentity, scaleAxis = 'x') => {
	/* See CH-1607 for more info */

	// Make sure ranges are the same
	const fromRange = fromScale.range();
	const toRange = toScale.range();
	if (!fromRange.every((value, i) => value === toRange[i])) toRange.range(fromRange);

	const fromDomain = fromScale.domain();
	const toDomain = toScale.domain();
	const translation = toScale(fromDomain[0]) - toScale(toDomain[0]);
	const k = (fromDomain[1] - fromDomain[0]) / (toDomain[1] - toDomain[0]);

	// Set correct translations
	const [tX, tY] = scaleAxis === 'x'
		? [translation, currentTransform[scaleAxis]]
		: [currentTransform[scaleAxis], translation];

	return zoomIdentity.translate(tX, tY).scale(k);
};

/**
 * Gets the current zoom transform for the chart.rootmouse node
 * @param {ModchartsInteractive} chart - Chart object to read the zoom transform from
 * @returns {Transform} The current zoom transform
 */
const getZoomTransform = chart => zoomTransform(chart.rootMouse.node());

/**
 * Programmatic pan
 * @param {object} chart - Modcharts chart instance
 * @param {number} distance - Pan distance in XAxis range pixels (positive goes left, negative goes right)
 * @param {number} duration - Animation duration in milliseconds
 */
const panChart = ({ chart, distance = -10, duration = chart.params.zoomDuration || 350 }) => {
	const { k: scale } = getZoomTransform(chart);
	if (!chart.state.zooming) { // This check is needed to ensure that a new event doesn't get triggered before the current one has finished
		if (duration === -1) { // This test needed for unit testing as transitions don't play well in unit tests
			chart.rootMouse
				.call(chart.zoom.translateBy, distance / scale, 0);
		} else {
			chart.rootMouse
				.transition()
				.duration(duration)
				.call(chart.zoom.translateBy, distance / scale, 0);
		}
	}
};

/**
 * Pans the chart back to the present view
 * @param {object} chart - Modcharts chart instance
 */
const panToPresent = chart => {
	// Calculate distance to get back to present view (but keep the current scale)
	const frameScale = chart.xAxis.primaryScale;
	const frameDomainStop = frameScale.domain()[1];
	const presentDomainStop = chart.state.xAxisPresentScale.domain()[1];
	const distance = frameScale(frameDomainStop) - frameScale(presentDomainStop);

	panChart({ chart, distance });
};

/**
 * Binds the zoom behavior to the rootMouse element
 * @param {object} chart - Modcharts chart instance
 */
const registerZoom = chart => {
	if (
		chart.params.zoomEnabled &&
		!chart.state.zoomRegistered &&
		!chart.state.tool
	) {
		chart.rootMouse
			.call(chart.zoom)
			.on('dblclick.zoom', null); // Disable double-click handling to make sure tool double clicking works
		chart.state.zoomRegistered = true;
	}
};

/**
 * Resets the zoom transform to the specified transform. If no transform is passed in, the initial transform
 * will be used. If no initial transform is available, the zoom identity ({ k: 1, x: 0, y: 0 }) will be used.
 * @param {object} chart - Modcharts chart instance
 * @param {number} duration - Animation duration in milliseconds
 * @param {object} transform - The D3 zoom transform to use for the reset
 */
const resetZoomCenter = ({
	chart,
	duration = chart.params.zoomResetDuration,
	transform = chart.state.initialTransform || zoomIdentity // Use zoomIdentity as fallback in case initialTransform hasn't been set
}) => {
	// Using a transition here will cause the chart to animate back to the given zoom state (initial by default) by causing a number of zoom events
	chart.rootMouse
		.transition()
		.duration(duration)
		.call(chart.zoom.transform, transform);
};

/**
 * Unbinds the zoom behavior from the rootMouse element
 * @param {object} chart - Modcharts chart instance
 */
const unregisterZoom = chart => {
	if (chart.state.zoomRegistered) {
		chart.rootMouse.on('.zoom', null);
		chart.state.zoomRegistered = false;
		chart.state.zooming = false;
	}
};

/**
 * Updates the zoom transform to the current zoom location. This is usually needed when
 * scales or new data have been loaded. If no transform is provided, the zoomIdentity
 * is used, effectively resetting the chart to the present view.
 * @param {object} chart - Modcharts chart instance
 * @param {object} transform - The D3 zoom transform to use for the update
 */
const updateZoomCenter = ({ chart, transform = zoomIdentity }) => {
	chart.state.domainAtZoomStart = null;
	chart.state.scaleAtZoomStart = null;
	// Calling transform without a transition results in setting the current location to represent  { k: 1, x: 0, y: 0 }
	chart.rootMouse.call(chart.zoom.transform, transform);
};

/**
 * Customize the distance zoomed during a WheelEvent
 * @param {object} chart - Modcharts chart instance
 * @param {object} d3event - D3 zoom event
 * @returns {number} - wheel delta
 */
const wheelDelta = (chart, d3event) => {
	chart.state.isWheelEvent = true; // will be set back to false on zoom end
	return -d3event.deltaY * (d3event.deltaMode === 1 ? 0.03 : d3event.deltaMode ? 1 : 0.002);
};

/**
 * Programmatic zoom
 * zoom in with a value like 1.3
 * zoom out with a value like (1 / 1.3)
 * @param {object} chart - Modcharts chart instance
 * @param {number} scale - Zoom distance in XAxis scale units
 * @param {number} duration - Animation duration in milliseconds
 */
const zoomChart = ({ chart, duration = chart.params.zoomDuration, scale = 1.3 }) => {
	if (!chart.state.zooming) { // This check is needed to ensure that a new event doesn't get triggered before the current one has finished
		if (duration === -1) { // This test needed for unit testing as transitions don't play well in unit tests
			chart.rootMouse
				.call(chart.zoom.scaleBy, scale);
		} else {
			chart.rootMouse
				.transition()
				.duration(duration)
				.call(chart.zoom.scaleBy, scale);
		}
	}
};

/**
 * Fires when zoom action begins
 * @param {Object} d3event - D3 zoom event
 */
const onZoomStart = (chart, d3event) => {
	// reset zoomreset
	window.clearTimeout(chart.zoomResetTimeout);

	// cancel and unregister zoom handling in specific circumstances
	if (
		chart.status === 0 ||
		chart.state.tool ||
		!chart.params.zoomEnabled ||
		chart.state.resizePanel
	) {
		unregisterZoom(chart);
		return;
	}

	// unselect any selected tools
	if (chart.selectTool) chart.selectTool(null);

	chart.state.zooming = true;

	// store date of zoom
	chart.state.zoomDate = new Date();

	// set closed_hand cursor if we're panning
	if (!chart.state.isWheelEvent) chart.setCursor(cursorTypes.CLOSED_HAND);

	// Store scale domain before zoom takes place
	if (chart.xAxis) chart.state.domainAtZoomStart = chart.xAxis.primaryScale.domain();

	chart.eventEmitter.emit(eventTypes.ZOOM_START, { d3event });
};

/**
 * Fires during a zoom
 * @param {object} d3event - D3 zoom event object
 */
const onZoom = (chart, d3event) => {

	if (chart.status === 0) return;

	if (chart.state.xAxisPresentScale) {
		// Update the scale by applying the transform to the present scale
		chart.xAxis.primaryScale = d3event.transform.rescaleX(chart.state.xAxisPresentScale);
	}

	if (!chart.state.resizePanel) chart.renderQueue();

	chart.eventEmitter.emit(eventTypes.ZOOM, { d3event });
};

/**
 * Fires when zoom action has finished
 * @param {object} d3event - D3 zoom event object
 */
const onZoomEnd = (chart, d3event) => {
	if (!chart.panels || chart.panels.length === 0 || !chart.state.domainAtZoomStart) return;

	chart.state.isWheelEvent = false;
	const domain = chart.xAxis.primaryScale.domain();
	const domainZoomStart = chart.state.domainAtZoomStart;
	const domainZoomStop = chart.xAxis.primaryScale.domain();
	const domainChanged =
		domainZoomStart[0] !== domainZoomStop[0] ||
		domainZoomStart[1] !== domainZoomStop[1];

	// set chart params dateStart/Stop to ensure that any backfills/polls return to the same domain range.
	// only alter dateStart/dateStop if domain has actually changed.
	if (chart.exchangeDates && domainChanged) {
		const domainFirst = chart.exchangeDates[Math.floor(domain[0])];
		const domainLast = chart.exchangeDates[Math.floor(domain[1])];

		if (domainFirst && domainLast) {
			chart.params.dateStart = new Date(domainFirst);
			chart.params.dateStop = new Date(domainLast);
		}

		if (chart.params.zoomReset) enforceZoomReset(chart);

		chart.normalizePanelDomains();

		// check if backfill request is needed
		if (
			chart.exchangeDates &&
			chart.status !== 0 &&
			chart.params.backfill &&
			!chart.params.customData // CH-4898 backfill is not supported for custom data
		) {
			chart.backfillData();
		}

		// a final render here fixes occasional mouseweheel quirks (NXC-429)
		chart.renderQueue();
	}

	chart.state.zooming = false;
	chart.setCursor(cursorTypes.CROSSHAIR);

	chart.eventEmitter.emit(eventTypes.ZOOM_END, d3event);
	chart.eventEmitter.emit(eventTypes.BOUNDARY_STATUS, chart._boundaryStatus);
};

/** Enforce zoom boundaries to either ruler edge or available data edge. */
const enforceZoomReset = chart => {
	const lastIndex = chart._calcLastIndex();

	if (lastIndex) {
		const currentDomain = chart.xAxis.primaryScale.domain();

		// Check if left/right edge of xAxis is out of bounds
		if (currentDomain[1] > lastIndex || currentDomain[0] < 0) {
			const domainChange = currentDomain[1] > lastIndex
				? currentDomain[1] - lastIndex // This would be a positive value
				: currentDomain[0]; // This would be a negative value
			const newRightDomain = currentDomain[1] - domainChange < lastIndex
				? currentDomain[1] - domainChange
				: lastIndex;

			const extendsLeft = Math.round(currentDomain[0]) < 0;
			const extendsRight = Math.round(currentDomain[1]) > lastIndex;

			// Chart has zoomed out such that we're past the edge on both sides
			if (extendsRight && extendsLeft) {
				const newTransform = getTransformFromScales(
					chart.state.xAxisPresentScale,
					// Copy present scale and set domain to the proper exchangeDates indices
					chart.state.xAxisPresentScale.copy().domain([0, lastIndex])
				);
				resetZoomCenter({ chart, transform: newTransform });
			} else if (extendsRight) {
				// Get current transform values
				const { k } = getZoomTransform(chart);

				const leftBoundaryPresentScale = chart.state.xAxisPresentScale(0);
				const rightBoundaryPresentScale = chart.state.xAxisPresentScale(newRightDomain);

				// Create new transform
				const { height, width, left } = chart.panels[0].size;
				const newXTranslation = width - (rightBoundaryPresentScale * k) + left;
				const newYTranslation = height - (height * k);
				let newTransform = zoomIdentity.translate(newXTranslation, newYTranslation).scale(k);

				// If the new translation would cause the chart to go out of bounds on the other side, zoom out to show all data
				if (newTransform.applyX(leftBoundaryPresentScale) > 0) {
					newTransform = getTransformFromScales(
						chart.state.xAxisPresentScale,
						// Copy present scale and set domain to the proper exchangeDates indices
						chart.state.xAxisPresentScale.copy().domain([0, lastIndex])
					);
				}

				// Move right edge back to data
				resetZoomCenter({ chart, transform: newTransform });
			} else if (extendsLeft) {
				// Get current transform values
				const { k } = getZoomTransform(chart);

				const leftBoundaryInitialScale = chart.state.xAxisPresentScale(0);
				const rightBoundaryInitialScale = chart.state.xAxisPresentScale(lastIndex);

				// Create new transform
				const { height, padding, width } = chart.panels[0].size;
				const newXTranslation = padding.left - (leftBoundaryInitialScale * k);
				const newYTranslation = height * (1 - k);
				let newTransform = zoomIdentity.translate(newXTranslation, newYTranslation).scale(k);

				// If the new translation would cause the chart to go out of bounds on the other side, zoom out to show all data
				if (newTransform.applyX(rightBoundaryInitialScale) < width) {
					newTransform = getTransformFromScales(
						chart.state.xAxisPresentScale,
						// Copy present scale and set domain to the proper exchangeDates indices
						chart.state.xAxisPresentScale.copy().domain([0, lastIndex])
					);
				}

				// Move left edge back to data
				resetZoomCenter({ chart, transform: newTransform });
			}
		}
	}
};

const zoomOutWithRightEdgeAnchor = (chart, scaleChange, duration = 300) => {
	const lastIndex = chart._calcLastIndex();
	const rightEdgeX = chart.xAxis.primaryScale(lastIndex); // X position of the last data point in data space

	// Get the current zoom transform
	const currentTransform = getZoomTransform(chart);
	const { x: currentX, y: currentY, k: currentK } = currentTransform;

	// Convert the right edge X position from data space to screen space
	const effectiveMouseX = rightEdgeX * currentK + currentX;

	// Calculate the new scale
	const newScale = currentK * scaleChange;

	// Calculate the new translation to keep the right edge as anchor
	const newTranslationX = effectiveMouseX - rightEdgeX * newScale;

	// Create the new transform
	const newTransform = zoomIdentity.translate(newTranslationX, currentY).scale(newScale);

	// Apply the new transform with a transition
	chart.rootMouse.transition()
		.duration(duration)
		.call(chart.zoom.transform, newTransform);
};

export { zoomIdentity };

export default {
	createZoom,
	enforceZoomReset,
	getTransformFromScales,
	getZoomTransform,
	onZoom,
	onZoomEnd,
	onZoomStart,
	panChart,
	panToPresent,
	registerZoom,
	resetZoomCenter,
	unregisterZoom,
	updateZoomCenter,
	zoomChart,
	zoomIdentity,
	zoomOutWithRightEdgeAnchor
};
